import React from "react";
import { heroData } from "@/features/hero/data/heroData";
import Button from "../../../components/shared/Button";
import FeatureItem from "./FeatureItem";
import TrustedLogos from "./TrustedLogos";
import { useTranslation, Trans } from "react-i18next";
import { FiArrowRight } from "react-icons/fi";

import MotionDiv from "@/components/animations/MotionDiv";
import { fadeUp, scaleUp, stagger } from "@/animations/variants";

import { useClients } from "@/hooks/useClients";

// Images
import heroImageEn from "/assets/images/hero-section.png";
import heroImageAr from "/assets/images/hero-section-ar.png";

const HeroSection = () => {
  const { t, i18n } = useTranslation();

  const isAR = i18n.resolvedLanguage?.startsWith("ar");
  const currentHeroImage = isAR ? heroImageAr : heroImageEn;

  const features = t("hero.features", { returnObjects: true });

  const { logos, loading: logosLoading, error: logosError } = useClients();

  return (
    <section
      id="home"
      dir={isAR ? "rtl" : "ltr"}
      className="relative flex min-h-screen items-center overflow-hidden bg-gradient-to-b from-[#C5E5FF] via-[#F7FBFE] to-[#F3F6FA] pt-24"
    >
      <div className="container mx-auto px-4">
        <div className="grid min-h-[450px] grid-cols-1 items-center gap-10 lg:grid-cols-12">
          {/* Left Content */}
          <div
            className={`lg:col-span-5 ${
              isAR ? "text-right" : "text-left"
            } text-center lg:text-start`}
          >
            {/* Title */}
            <MotionDiv variants={fadeUp}>
              <h1 className="mx-auto max-w-xl text-4xl leading-tight font-semibold text-[#202B3D] md:text-5xl lg:mx-0 lg:text-[54px]">
                <Trans
                  i18nKey="hero.title"
                  components={{
                    highlight: <span className="text-primary" />,
                  }}
                />
              </h1>
            </MotionDiv>

            {/* Buttons */}
            <MotionDiv
              variants={fadeUp}
              className={`mt-10 flex flex-col items-center gap-4 sm:flex-row ${
                isAR ? "lg:justify-end" : "lg:justify-start"
              } justify-center`}
            >
              <Button
                url={heroData.ctaPrimary.url}
                variant="primary"
                className="w-full sm:w-auto"
                rightIcon={<FiArrowRight />}
              >
                {t("hero.ctaPrimary")}
              </Button>

              <Button
                url={heroData.ctaSecondary.url}
                variant="secondary"
                className="w-full sm:w-auto"
              >
                {t("hero.ctaSecondary")}
              </Button>
            </MotionDiv>
          </div>

          {/* Right Image */}
          <MotionDiv
            variants={scaleUp}
            className="flex justify-center lg:col-span-7"
          >
            <div className="relative w-full max-w-[820px]">
              <img
                src={currentHeroImage}
                alt={t("hero.imageAlt", "HR software dashboard preview")}
                loading="eager"
                className="h-auto w-full object-contain"
              />
            </div>
          </MotionDiv>
        </div>

        {/* Features */}
        <MotionDiv
          variants={stagger}
          className="mt-6 grid grid-cols-1 gap-5 pb-1 sm:grid-cols-2 lg:grid-cols-3"
        >
          {Array.isArray(features) &&
            features.map((item, i) => (
              <MotionDiv key={i} variants={fadeUp}>
                <div className="flex justify-center lg:justify-start">
                  <FeatureItem text={item} />
                </div>
              </MotionDiv>
            ))}
        </MotionDiv>

        {/* Trusted Logos From Backend */}
        {!logosLoading && !logosError && logos.length > 0 && (
          <TrustedLogos title={t("hero.trustedTitle")} logos={logos} />
        )}
      </div>
    </section>
  );
};

export default HeroSection;
